import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { guidesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Calendar, CheckCircle, RefreshCw, 
  Target, Clock, Users, Star, Award, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const First90DaysPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(0);
  const [completedTasks, setCompletedTasks] = useState([]);

  useEffect(() => {
    fetchGuide();
    // Load completed tasks from localStorage
    const saved = localStorage.getItem('first90_completed');
    if (saved) {
      setCompletedTasks(JSON.parse(saved));
    }
  }, []);

  const fetchGuide = async () => {
    try {
      const res = await guidesAPI.getFirst90Days();
      setGuide(res.data);
    } catch (error) {
      console.error('Failed to fetch guide:', error);
      toast.error('Failed to load guide');
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = (taskId) => {
    let updated;
    if (completedTasks.includes(taskId)) {
      updated = completedTasks.filter(id => id !== taskId);
    } else {
      updated = [...completedTasks, taskId];
    }
    setCompletedTasks(updated);
    localStorage.setItem('first90_completed', JSON.stringify(updated));
  };

  const getPhaseProgress = (phase) => {
    if (!phase || !phase.tasks) return 0;
    const phaseTasks = phase.tasks.map((_, i) => `${phase.name}-${i}`);
    const completed = phaseTasks.filter(id => completedTasks.includes(id)).length;
    return Math.round((completed / phaseTasks.length) * 100);
  };

  const getPhaseIcon = (index) => {
    switch(index) {
      case 0: return <Target className="w-5 h-5" />;
      case 1: return <Users className="w-5 h-5" />;
      case 2: return <Star className="w-5 h-5" />;
      default: return <Calendar className="w-5 h-5" />;
    }
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">First 90 Days Guide</h2>
          <p className="text-slate-600 mb-6">Your roadmap to success in a new job. Subscribe to access this feature.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">First 90 Days Guide</h1>
            <p className="text-sm text-slate-500">Your roadmap to success in a new job</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 lg:p-8 text-white mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Welcome to Your New Job!</h2>
              <p className="text-blue-100">Follow this guide to make a great impression</p>
            </div>
          </div>
          
          {/* Overall Progress */}
          <div className="bg-white/20 rounded-xl p-4">
            <div className="flex justify-between text-sm mb-2">
              <span>Overall Progress</span>
              <span>{completedTasks.length} tasks completed</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full">
              <div 
                className="h-3 bg-white rounded-full transition-all duration-500"
                style={{ width: `${guide?.phases ? Math.round((completedTasks.length / guide.phases.reduce((acc, p) => acc + p.tasks.length, 0)) * 100) : 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Phase Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {guide?.phases?.map((phase, index) => (
              <button
                key={index}
                onClick={() => setActivePhase(index)}
                className={`flex-1 min-w-[140px] px-4 py-4 text-center transition-colors ${
                  activePhase === index 
                    ? 'bg-blue-50 border-b-2 border-blue-600 text-blue-700' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`phase-tab-${index}`}
              >
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    activePhase === index ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    {getPhaseIcon(index)}
                  </div>
                  <span className="font-medium text-sm">{phase.name}</span>
                  <span className="text-xs text-slate-500">{phase.duration}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Phase Content */}
          {guide?.phases?.[activePhase] && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{guide.phases[activePhase].name}</h3>
                  <p className="text-sm text-slate-500">{guide.phases[activePhase].description}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-blue-600">
                    {getPhaseProgress(guide.phases[activePhase])}%
                  </span>
                  <p className="text-xs text-slate-500">complete</p>
                </div>
              </div>

              {/* Goals */}
              {guide.phases[activePhase].goals && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Phase Goals
                  </h4>
                  <ul className="space-y-1">
                    {guide.phases[activePhase].goals.map((goal, i) => (
                      <li key={i} className="text-sm text-blue-700">• {goal}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tasks */}
              <div className="space-y-3">
                {guide.phases[activePhase].tasks?.map((task, i) => {
                  const taskId = `${guide.phases[activePhase].name}-${i}`;
                  const isCompleted = completedTasks.includes(taskId);
                  
                  return (
                    <div
                      key={i}
                      onClick={() => toggleTask(taskId)}
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isCompleted 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`task-${taskId}`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isCompleted 
                          ? 'bg-emerald-500 border-emerald-500' 
                          : 'border-slate-300'
                      }`}>
                        {isCompleted && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${isCompleted ? 'text-emerald-800 line-through' : 'text-slate-900'}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className={`text-sm mt-1 ${isCompleted ? 'text-emerald-600' : 'text-slate-500'}`}>
                            {task.description}
                          </p>
                        )}
                        {task.tips && (
                          <div className="mt-2 flex items-start gap-2 text-xs text-slate-500">
                            <Lightbulb className="w-4 h-4 flex-shrink-0" />
                            <span>{task.tips}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Tips Section */}
        {guide?.general_tips && (
          <div className="bg-slate-900 rounded-2xl p-6 text-white">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              General Tips for Success
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {guide.general_tips.map((tip, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-4">
                  <p className="text-slate-100">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default First90DaysPage;
